import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, Divider, Typography, Switch, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  WebOutlined, AddOutlined, DeleteOutlineOutlined,
  DragIndicatorOutlined, CheckOutlined, LinkOutlined,
} from '@mui/icons-material';
import { PRIMARY } from '../constants';
import { InspectorSection, StyleRow, StyleInput, ColorInput, PanelHeader } from '../../eventPagesTab/ui/primitives';

// ── isUrl helper ──────────────────────────────────────────────────────────────
const isUrl = str => {
  if (!str) return false;
  return /^https?:\/\//i.test(str.trim()) || /^www\./i.test(str.trim());
};

function uid() { return Math.random().toString(36).slice(2, 9); }

function SectionDivider() {
  return <Divider sx={{ borderColor: '#f0f2f5', my: 0.5 }} />;
}

const selectSx = {
  border: '1px solid #e4e9f0', borderRadius: '6px',
  px: 1, py: '4px', fontSize: '0.72rem', fontFamily: 'inherit',
  background: '#f8f9fb', color: '#1a1a2e', outline: 'none',
  cursor: 'pointer', width: '100%',
  '&:focus': { borderColor: PRIMARY },
};

// ── Template picker ───────────────────────────────────────────────────────────
function TemplatePicker({ value, onChange }) {
  const templates = [
    {
      key: 'classic',
      label: 'Classic',
      desc: 'Full-width bar',
      preview: (
        <Box sx={{ width: '100%', height: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, background: '#fff', border: '1px solid #e8edf2' }}>
          <Box sx={{ width: 28, height: 10, borderRadius: 1, background: '#d4dbe6' }} />
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <Box sx={{ width: 36, height: 16, borderRadius: 1, background: PRIMARY, opacity: 0.85 }} />
          </Box>
        </Box>
      ),
    },
    {
      key: 'pill',
      label: 'Pill',
      desc: 'Floating pill container',
      preview: (
        <Box sx={{ width: '100%', height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1, background: '#eef3f8' }}>
          <Box sx={{ flex: 1, height: 24, borderRadius: '999px', background: '#fff', border: '1px solid #e0e6ee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, gap: 1, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
            <Box sx={{ width: 22, height: 8, borderRadius: 1, background: '#d4dbe6' }} />
            <Box sx={{ width: 36, height: 16, borderRadius: '999px', background: PRIMARY, opacity: 0.85 }} />
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {templates.map(t => {
        const active = value === t.key;
        return (
          <Box key={t.key} onClick={() => onChange(t.key)} sx={{
            width: '100%', cursor: 'pointer', borderRadius: 1.5, p: 1,
            border: active ? `2px solid ${PRIMARY}` : '2px solid #e8edf2',
            background: active ? alpha(PRIMARY, 0.03) : '#fafbfc',
            transition: 'all 0.15s',
            '&:hover': { borderColor: alpha(PRIMARY, 0.4) },
          }}>
            {t.preview}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: active ? 700 : 600, color: active ? PRIMARY : '#5a6474' }}>{t.label}</Typography>
                <Typography sx={{ fontSize: '0.63rem', color: '#9ea8b3', mt: 0.1 }}>{t.desc}</Typography>
              </Box>
              {active && <CheckOutlined sx={{ fontSize: 13, color: PRIMARY }} />}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Inline-editable link label ────────────────────────────────────────────────
function InlineLabel({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onChange(trimmed);
    else setDraft(value);
  };

  if (editing) {
    return (
      <Box component="input" ref={inputRef} value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
        onClick={e => e.stopPropagation()}
        sx={{ flex: 1, border: 'none', borderBottom: `1px solid ${PRIMARY}`, outline: 'none', background: 'transparent', fontSize: '0.75rem', fontWeight: 500, color: '#1a1a2e', fontFamily: 'inherit', p: 0, minWidth: 0 }}
      />
    );
  }

  return (
    <Typography onDoubleClick={e => { e.stopPropagation(); setEditing(true); }} title="Double-click to rename"
      sx={{ flex: 1, fontSize: '0.75rem', fontWeight: 500, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text', minWidth: 0 }}>
      {value || 'Untitled link'}
    </Typography>
  );
}

// ── LobbyPickerPopup (inline version for inspector — portalled to body) ─────
function InspectorLobbyPicker({ lobbies, currentLobbyId, anchorEl, onSelect, onClose }) {
  const popRef = useRef(null);
  const [query, setQuery] = useState('');
  const [pos, setPos]     = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const popW = 280;
    let left = r.left;
    if (left + popW > window.innerWidth - 8) left = window.innerWidth - popW - 8;
    setPos({ top: r.bottom + 4, left });
  }, [anchorEl]);

  useEffect(() => {
    const handler = e => {
      if (popRef.current && !popRef.current.contains(e.target) &&
          anchorEl && !anchorEl.contains(e.target)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [onClose, anchorEl]);

  const filtered = lobbies.filter(l =>
    l.name?.toLowerCase().includes(query.toLowerCase())
  );

  return ReactDOM.createPortal(
    <div
      ref={popRef}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', top: pos.top, left: pos.left,
        width: 280, maxHeight: 320,
        background: '#fff', border: '1px solid #e4e9f0',
        borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 99999,
      }}
    >
      {/* Search */}
      <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search lobbies…"
          style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e4e9f0', borderRadius: 7, padding: '6px 10px', fontSize: 12.5, outline: 'none', background: '#f8f9fb', fontFamily: 'inherit' }}
        />
      </div>
      {/* Lobby list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 6px' }}>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 12, color: '#9ea8b3' }}>No lobbies found</div>
        )}
        {filtered.map(lobby => {
          const isSelected = lobby._id === currentLobbyId;
          const bg = lobby.bgUrl || null;
          return (
            <div
              key={lobby._id}
              onClick={() => { onSelect(lobby); onClose(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 8px', borderRadius: 8, cursor: 'pointer',
                background: isSelected ? alpha(PRIMARY, 0.07) : 'transparent',
                border: `1px solid ${isSelected ? PRIMARY : 'transparent'}`,
                marginBottom: 2, transition: 'all 0.12s',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f5f7fa'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Thumbnail */}
              <div style={{
                width: 36, height: 24, borderRadius: 5, flexShrink: 0,
                background: bg ? `url(${bg}) center/cover` : alpha(PRIMARY, 0.12),
                border: '1px solid #e4e9f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {!bg && <LinkOutlined style={{ fontSize: 13, color: PRIMARY, opacity: 0.5 }} />}
              </div>
              <div style={{ fontSize: 12.5, fontWeight: isSelected ? 700 : 500, color: isSelected ? PRIMARY : '#2c3444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {lobby.name}
              </div>
              {isSelected && <CheckOutlined style={{ fontSize: 13, color: PRIMARY, marginLeft: 'auto', flexShrink: 0 }} />}
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );
}

// ── Single nav link row ───────────────────────────────────────────────────────
function NavLinkRow({ link, lobbies, onChange, onDelete }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerBtnRef = useRef(null);
  const update = patch => onChange({ ...link, ...patch });

  const targetLobby = link.lobbyId
    ? lobbies.find(l => l._id === link.lobbyId) || null
    : null;

  return (
    <Box sx={{ border: '1px solid #e8edf2', borderRadius: 1.5, overflow: 'visible', background: '#fff', position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.8 }}>
        <DragIndicatorOutlined sx={{ fontSize: 13, color: '#d4dbe6', cursor: 'grab', flexShrink: 0 }} />

        {/* Lobby thumbnail */}
        {targetLobby ? (
          <Box
            ref={pickerBtnRef}
            onClick={() => setPickerOpen(v => !v)}
            title="Change lobby"
            sx={{
              width: 28, height: 20, borderRadius: '4px', flexShrink: 0,
              background: targetLobby.bgUrl ? `url(${targetLobby.bgUrl}) center/cover` : alpha(PRIMARY, 0.12),
              border: `1.5px solid ${pickerOpen ? PRIMARY : '#e4e9f0'}`,
              cursor: 'pointer', transition: 'border-color 0.13s',
              '&:hover': { borderColor: alpha(PRIMARY, 0.5) },
            }}
          />
        ) : (
          <Box
            ref={pickerBtnRef}
            onClick={() => setPickerOpen(v => !v)}
            title="Pick lobby"
            sx={{
              width: 28, height: 20, borderRadius: '4px', flexShrink: 0,
              background: pickerOpen ? alpha(PRIMARY, 0.1) : '#f0f4f8',
              border: `1.5px solid ${pickerOpen ? PRIMARY : '#e4e9f0'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.13s',
              '&:hover': { borderColor: alpha(PRIMARY, 0.5) },
            }}
          >
            <LinkOutlined sx={{ fontSize: 12, color: PRIMARY, opacity: 0.5 }} />
          </Box>
        )}

        {/* Inline editable label */}
        <InlineLabel value={link.label || ''} onChange={v => update({ label: v })} />

        {/* Delete */}
        <Tooltip title="Remove link">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(); }}
            sx={{ p: 0.3, flexShrink: 0, color: '#c0c8d4', '&:hover': { color: '#dc2626' } }}>
            <DeleteOutlineOutlined sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Lobby name chip below if set */}
      {targetLobby && (
        <Box sx={{ px: 1, pb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: PRIMARY, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.65rem', color: '#9ea8b3' }}>→ {targetLobby.name}</Typography>
        </Box>
      )}

      {/* Picker popup */}
      {pickerOpen && (
        <InspectorLobbyPicker
          lobbies={lobbies}
          currentLobbyId={link.lobbyId}
          anchorEl={pickerBtnRef.current}
          onSelect={lobby => update({ lobbyId: lobby._id, label: link.label || lobby.name, href: '' })}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </Box>
  );
}

// ── Links list ────────────────────────────────────────────────────────────────
function LinksEditor({ links, lobbies, onChange }) {
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const addBtnRef = useRef(null);

  const addLink    = (lobby) => onChange([...links, { id: uid(), label: lobby.name, lobbyId: lobby._id, href: '', newTab: false }]);
  const updateLink = (i, patch) => onChange(links.map((l, idx) => idx === i ? patch : l));
  const deleteLink = (i) => onChange(links.filter((_, idx) => idx !== i));

  return (
    <InspectorSection label="Navigation Links">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {links.length === 0 && (
          <Box
            ref={addBtnRef}
            onClick={() => setAddPickerOpen(v => !v)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
              py: 1.25, borderRadius: 1.5, border: '1.5px dashed #c4cdd8', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 600, color: '#9ea8b3',
              transition: 'all 0.15s',
              '&:hover': { borderColor: PRIMARY, color: PRIMARY, background: alpha(PRIMARY, 0.04) },
            }}
          >
            <AddOutlined sx={{ fontSize: 14 }} />
            Add lobby link
          </Box>
        )}
        {links.map((link, i) => (
          <NavLinkRow
            key={link.id || i}
            link={link}
            lobbies={lobbies}
            onChange={patch => updateLink(i, patch)}
            onDelete={() => deleteLink(i)}
          />
        ))}
        {links.length > 0 && (
          <Box
            ref={addBtnRef}
            onClick={() => setAddPickerOpen(v => !v)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              py: 0.7, borderRadius: 1.5, border: '1px dashed #c4cdd8', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 600, color: '#9ea8b3', mt: 0.25,
              transition: 'all 0.15s',
              '&:hover': { borderColor: PRIMARY, color: PRIMARY, background: alpha(PRIMARY, 0.04) },
            }}
          >
            <AddOutlined sx={{ fontSize: 14 }} />
            Add link
          </Box>
        )}
        {addPickerOpen && (
          <InspectorLobbyPicker
            lobbies={lobbies}
            currentLobbyId={null}
            anchorEl={addBtnRef.current}
            onSelect={lobby => { addLink(lobby); setAddPickerOpen(false); }}
            onClose={() => setAddPickerOpen(false)}
          />
        )}
      </Box>
    </InspectorSection>
  );
}

// ── Color row ─────────────────────────────────────────────────────────────────
function ColorRow({ label, value, onChange }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: '0.68rem', color: '#7a8494', mb: 0.4, fontWeight: 500 }}>{label}</Typography>
      <ColorInput value={value} onChange={onChange} />
    </Box>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export function LobbyHeaderInspector({ config = {}, onChange, onClose, lobbies = [], activeLobby, onChangeLobby }) {
  const update      = patch => onChange({ ...config, ...patch });
  const updateStyle = patch => update({ style: { ...(config.style || {}), ...patch } });

  const isBooth    = activeLobby?.lobbyType === 'booth';
  const toggleBooth = () => {
    const next = !isBooth;
    onChangeLobby?.({ lobbyType: next ? 'booth' : 'lobby' });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader icon={<WebOutlined sx={{ fontSize: 14 }} />} label="Lobby Header" color={PRIMARY} onClose={onClose} />

      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>



        {/* ── Template ── */}
        <InspectorSection label="Template">
          <TemplatePicker
            value={config.template || 'classic'}
            onChange={v => update({ template: v })}
          />
        </InspectorSection>

        <SectionDivider />

        {/* ── Account button ── */}
        <InspectorSection label="Account Button">
          <StyleRow label="Label">
            <StyleInput value={config.accountLabel || ''} onChange={v => update({ accountLabel: v })} placeholder="My Account" />
          </StyleRow>
          <Typography sx={{ fontSize: '0.67rem', color: '#9ea8b3', mt: 0.5 }}>
            In live mode the logged-in participant's name is shown automatically.
          </Typography>
        </InspectorSection>

        <SectionDivider />

        {/* ── Colors ── */}
        <InspectorSection label="Colors">
          <ColorRow
            label="Background"
            value={config.style?.background || '#ffffff'}
            onChange={v => updateStyle({ background: v })}
          />
          <ColorRow
            label="Nav link color"
            value={config.style?.linkColor || '#1a1a2e'}
            onChange={v => updateStyle({ linkColor: v })}
          />
          <ColorRow
            label="Account button background"
            value={config.style?.accountBg || PRIMARY}
            onChange={v => updateStyle({ accountBg: v })}
          />
          <ColorRow
            label="Account button text"
            value={config.style?.accountColor || '#ffffff'}
            onChange={v => updateStyle({ accountColor: v })}
          />
          {config.template === 'pill' && (
            <ColorRow
              label="Pill background"
              value={config.style?.pillBg || '#ffffff'}
              onChange={v => updateStyle({ pillBg: v })}
            />
          )}
        </InspectorSection>

      </Box>
    </Box>
  );
}

export default LobbyHeaderInspector;